import React, { useState, useEffect } from 'react';
import { Mail, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import {
  defaultActivitySummary,
  outcomeSuggestsFollowUpDays,
  addDaysIso,
} from '../../config/salesCrm';

const API = API_BASE;

function defaultSubject(lead) {
  const company = lead?.company_name || 'your care home';
  return `Staffing support for ${company}`;
}

/**
 * Send outreach email through Osabea (Resend) — logged automatically on the lead timeline.
 */
export default function LeadEmailComposer({ lead, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!lead) return;
    setSubject(defaultSubject(lead));
    setBody(defaultActivitySummary('email', lead));
  }, [lead?.id, lead?.company_name, lead?.contact_name]);

  if (lead?.do_not_contact) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        Outreach blocked — lead is marked do not contact.
      </div>
    );
  }

  if (!lead?.contact_email?.trim()) {
    return (
      <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <p className="font-medium flex items-center gap-2"><Mail className="h-4 w-4" /> Send from Osabea</p>
        <p className="mt-1 text-xs">Add a contact email above to send directly from the portal (no Outlook needed).</p>
      </div>
    );
  }

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/sales-leads/${lead.id}/send-email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: subject.trim(),
          body: body.trim(),
          next_follow_up_at: addDaysIso(outcomeSuggestsFollowUpDays('email_sent')),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Email sent to ${data.recipient}`);
        onSent?.(data.sales_lead);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send email');
      }
    } catch {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
      <div>
        <p className="text-sm font-medium flex items-center gap-2 text-primary">
          <Mail className="h-4 w-4" />
          Send from Osabea
        </p>
        <p className="text-xs text-text-muted mt-0.5">
          Branded email with Osabea logo from staffing@osabeacares.co.uk → {lead.contact_email}. Replies land in Portal Inbox → Staffing.
        </p>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Subject</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Message</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} />
      </div>
      <Button onClick={handleSend} disabled={sending} className="w-full">
        <Send className="h-4 w-4 mr-2" />
        {sending ? 'Sending…' : 'Send email from portal'}
      </Button>
    </div>
  );
}